import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { refreshServerBundles } from "@/i18n";
import {
  listTranslations,
  machineTranslateField,
  machineTranslateSection,
  publishTranslations,
  upsertTranslation,
} from "@/services/api/i18n";
import type { TranslationQuery } from "@/services/api/i18n";

/** Paginated, searchable translation workbench queries and mutations. */
export function useTranslations(query: TranslationQuery) {
  return useQuery({
    queryKey: ["translations", query],
    queryFn: () => listTranslations(query),
  });
}

export function useUpsertTranslation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { key: string; language_code: string; value: string }) =>
      upsertTranslation(args.key, {
        language_code: args.language_code,
        value: args.value,
        is_machine_translated: false,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["translations"] }),
  });
}

export function useMachineTranslateField() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { key: string; target_language: string }) =>
      machineTranslateField(args.key, args.target_language),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["translations"] }),
  });
}

export function useMachineTranslateSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { namespace: string; target_language?: string }) =>
      machineTranslateSection(args.namespace, args.target_language),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["translations"] }),
  });
}

export function usePublishTranslations() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => publishTranslations(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["languages"] });
      // Pull the freshly published bundles into the running SPA so edits take
      // effect immediately (no manual refresh required).
      void refreshServerBundles();
    },
  });
}
